/**
 * @file: LeadActivityTimeline.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Typography } from '@euler/app/components/typography';
import { LeadsNavParams } from '@euler/app/flows/leads';
import {
  renderLeadActivityItemNames,
  renderLeadActivityTitle,
} from '@euler/app/flows/leads/pages/detail/functions/renderLeadActivity';
import { Icon, IconType } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  LeadActivityChannel,
  LeadActivityView,
} from '@euler/model/lead/LeadActivity';
import {
  LeadItemDecision,
  LeadItemDecisionLabel,
} from '@euler/model/lead/LeadItem';
import { dateFromValue } from '@euler/utils/datetime';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import format from 'date-fns/format';
import { ChevronRight } from 'lucide-react-native';
import { memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { MediaAttachmentGrid } from '../MediaAttachmentGrid';
import { LD } from '../leadDetailKit';

const formatTime = (value: string | Date): string => {
  try {
    return format(dateFromValue(value), 'MM-dd HH:mm');
  } catch {
    return '';
  }
};

// The channel a user-logged outreach went through, shown as the timeline node.
const channelIconName: Record<LeadActivityChannel, IconType | null> = {
  phone: 'phone',
  sms: 'mobile-phone',
  wechat: 'wechat',
  in_person: 'home',
  note: 'note',
  system: null,
};

// The timeline node glyph for entries that warrant one: an outreach shows its
// channel icon (brand), a decision shows a colour-matched verdict icon. Others
// fall back to the plain dot.
const nodeIcon = (
  activity: LeadActivityView,
): { icon: IconType; bg: string; fg: string } | null => {
  const data = activity.data;
  if (data?.type === 'outreach') {
    const icon = channelIconName[data.channel];
    return icon ? { icon, bg: LD.brandSoft, fg: LD.brand } : null;
  }
  if (data?.type === 'item_decision_set') {
    if (data.decision === 'will_fix') {
      return { icon: 'check', bg: LD.successSoft, fg: LD.success };
    }
    if (data.decision === 'declined') {
      return { icon: 'clock', bg: LD.amberSoft, fg: LD.amber };
    }
    return { icon: 'arrow-undo-circle-outline', bg: LD.hairline, fg: LD.mist };
  }
  // A scheduled reminder rings a bell — brand blue for the internal staff
  // nudge, green for the customer-facing reminder, so the two audiences read
  // apart at a glance.
  if (data?.type === 'reminder_sent') {
    return data.audience === 'staff'
      ? { icon: 'notification', bg: LD.brandSoft, fg: LD.brand }
      : { icon: 'notification', bg: LD.successSoft, fg: LD.success };
  }
  return null;
};

// A decision entry's chip colors — green = confirmed, amber = postponed, and a
// neutral chip when the intent was cleared back to 待确认.
const decisionChipStyle = (
  decision: LeadItemDecision | null,
): { label: string; bg: string; fg: string } => {
  if (decision === 'will_fix') {
    return {
      label: LeadItemDecisionLabel.will_fix,
      bg: LD.successSoft,
      fg: LD.success,
    };
  }
  if (decision === 'declined') {
    return {
      label: LeadItemDecisionLabel.declined,
      bg: LD.amberSoft,
      fg: LD.amber,
    };
  }
  return { label: '清除意向', bg: LD.hairline, fg: LD.mist };
};

const Chip = ({ text, bg, fg }: { text: string; bg: string; fg: string }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        backgroundColor: bg,
        borderRadius: rpx(11),
        paddingHorizontal: rpx(9),
        paddingVertical: rpx(3),
      }}
    >
      <Typography.Text size={rpx(13)} color={fg} medium>
        {text}
      </Typography.Text>
    </View>
  );
};

// A customer-decision entry, rendered as [item] → [decision] chips rather than
// a flat sentence so the decision colour reads at a glance.
const DecisionActivity = memo(
  ({ name, decision }: { name: string; decision: LeadItemDecision | null }) => {
    const { rpx } = useResponsiveContext();
    const d = decisionChipStyle(decision);
    return (
      <View
        style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}
      >
        <Chip text={name} bg={LD.hairline} fg={LD.steel} />
        <Typography.Text
          size={rpx(13)}
          color={LD.mist}
          style={{ marginHorizontal: rpx(6) }}
        >
          →
        </Typography.Text>
        <Chip text={d.label} bg={d.bg} fg={d.fg} />
      </View>
    );
  },
);

// A "view reservation" pill on the `reservation_created` entry. Opens the
// reservation detail embedded in this lead stack (the private `_reservation`
// route) so it can navigate back to the lead.
const ReservationLink = memo(({ reservationId }: { reservationId: string }) => {
  const { rpx } = useResponsiveContext();
  const navigation = useNavigation<StackNavigationProp<LeadsNavParams>>();
  const open = useCallback(() => {
    navigation.navigate('_reservation', { id: reservationId });
  }, [navigation, reservationId]);
  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={open}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        marginTop: rpx(6),
        paddingVertical: rpx(4),
        paddingHorizontal: rpx(10),
        borderRadius: rpx(14),
        backgroundColor: LD.brandSoft,
      }}
    >
      <Typography.Text size={rpx(12)} color={LD.brand} medium>
        查看预约
      </Typography.Text>
      <ChevronRight
        size={rpx(13)}
        color={LD.brand}
        style={{ marginLeft: rpx(1) }}
      />
    </TouchableOpacity>
  );
});

export const LeadActivityTimeline = memo(
  ({ activities }: { activities: LeadActivityView[] }) => {
    const { rpx } = useResponsiveContext();
    if (!activities.length) {
      return (
        <Typography.Text
          size={rpx(13)}
          color={LD.mist}
          style={{ paddingVertical: rpx(12) }}
        >
          暂无跟进动态
        </Typography.Text>
      );
    }
    return (
      <View style={{ paddingVertical: rpx(8) }}>
        {activities.map((activity, index) => {
          const last = index === activities.length - 1;
          // A manual follow-up is a filled brand node; a system event is a
          // hollow node — the human touches stand out from the automated log.
          const manual = activity.kind === 'manual';
          const itemNames = renderLeadActivityItemNames(activity);
          const data = activity.data;
          // Outreach and decision entries carry a node icon; other entries keep
          // the dot (filled = manual, hollow = system event).
          const node = nodeIcon(activity);
          return (
            <View key={activity.id} style={{ flexDirection: 'row' }}>
              <View style={{ alignItems: 'center', width: rpx(26) }}>
                {node ? (
                  <View
                    style={{
                      width: rpx(24),
                      height: rpx(24),
                      borderRadius: rpx(12),
                      backgroundColor: node.bg,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name={node.icon} size={rpx(13)} color={node.fg} />
                  </View>
                ) : (
                  <View
                    style={{
                      width: rpx(10),
                      height: rpx(10),
                      borderRadius: rpx(5),
                      marginTop: rpx(4),
                      backgroundColor: manual ? LD.brand : LD.surface,
                      borderWidth: manual ? rpx(2) : rpx(1.5),
                      borderColor: manual ? LD.brandSoft : LD.mist,
                    }}
                  />
                )}
                {!last ? (
                  <View
                    style={{
                      flex: 1,
                      width: 1,
                      backgroundColor: LD.hairline,
                      marginTop: rpx(2),
                    }}
                  />
                ) : null}
              </View>
              <View
                style={{
                  flex: 1,
                  marginLeft: rpx(6),
                  paddingBottom: rpx(15),
                }}
              >
                {data?.type === 'item_decision_set' ? (
                  <DecisionActivity
                    name={data.item.name}
                    decision={data.decision}
                  />
                ) : (
                  <Typography.Text size={rpx(14)} color={LD.ink} medium>
                    {renderLeadActivityTitle(activity)}
                  </Typography.Text>
                )}
                {itemNames ? (
                  <Typography.Text
                    size={rpx(13)}
                    color={LD.steel}
                    mt={rpx(3)}
                    lineHeight={rpx(19)}
                  >
                    {itemNames}
                  </Typography.Text>
                ) : null}
                {activity.notes ? (
                  <Typography.Text
                    size={rpx(13)}
                    color={LD.steel}
                    mt={rpx(3)}
                    lineHeight={rpx(19)}
                  >
                    {activity.notes}
                  </Typography.Text>
                ) : null}
                {activity.attachments?.length ? (
                  <MediaAttachmentGrid attachments={activity.attachments} />
                ) : null}
                {data?.type === 'reservation_created' ? (
                  <ReservationLink reservationId={data.reservationId} />
                ) : null}
                <Typography.Text size={rpx(11)} color={LD.mist} mt={rpx(3)}>
                  {activity.createdByName} · {formatTime(activity.createdAt)}
                </Typography.Text>
              </View>
            </View>
          );
        })}
      </View>
    );
  },
);
